import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  FiEdit2, FiCheck, FiX, FiPhone, FiMail,
  FiBriefcase, FiClock, FiAward, FiChevronRight
} from 'react-icons/fi'
import http from '../services/http'
import { interviewService } from '../services/interviewService'
import defaultProfile from '../assets/img/default-profile.png'
import '../scss/profile.scss'
import { resolveInterviewTimestampMeta } from '../utils/interviewTimestamp'
import CandidateProfileEditor from '../components/CandidateProfileEditor'

interface UserProfile {
  user_id: number
  fullname: string
  email: string
  phone: string
  [key: string]: any
}

interface InterviewItem {
  id: number
  status: 'in_progress' | 'technical_interrupted' | 'completed'
  created_at: string
  started_at?: string
  completed_at?: string
  job?: { id?: number; title?: string; location?: { name?: string } }
  result?: { final_score: number; status: string }
}

// ── Profile completion logic ──────────────────────────────────
function calcCompletion(user: UserProfile, interviewCount: number, completedCount: number) {
  const steps = [
    { label: 'Data diri lengkap', done: !!(user.fullname && user.phone) },
    { label: 'Pernah melamar pekerjaan', done: interviewCount > 0 },
    { label: 'Menyelesaikan interview', done: completedCount > 0 },
  ]
  const pct = Math.round((steps.filter(s => s.done).length / steps.length) * 100)
  return { steps, pct }
}

function badgeClass(status: string) {
  if (status === 'strong') return 'prf-badge prf-badge--strong'
  if (status === 'average') return 'prf-badge prf-badge--average'
  if (status === 'weak') return 'prf-badge prf-badge--weak'
  return 'prf-badge prf-badge--pending'
}

function badgeLabel(iv: InterviewItem) {
  if (iv.status === 'in_progress') return 'Berlangsung'
  if (iv.status === 'technical_interrupted') return 'Terkendala Teknis'
  if (!iv.result) return 'Selesai'
  const s = iv.result.status
  if (s === 'strong') return 'Kuat'
  if (s === 'average') return 'Cukup'
  if (s === 'weak') return 'Lemah'
  return 'Selesai'
}

const Profile = () => {
  const navigate = useNavigate()
  const [user, setUser] = useState<UserProfile | null>(null)
  const [isEditing, setIsEditing] = useState(false)
  const [form, setForm] = useState({ fullname: '', phone: '' })
  const [saving, setSaving] = useState(false)
  const [toast, setToast] = useState<{ type: 'success' | 'error'; msg: string } | null>(null)
  const [interviews, setInterviews] = useState<InterviewItem[]>([])
  const [applicationCount, setApplicationCount] = useState(0)
  const [ivLoading, setIvLoading] = useState(true)

  // Load user from localStorage
  useEffect(() => {
    const raw = localStorage.getItem('authUser')
    if (!raw) { navigate('/login'); return }
    try {
      const parsed: UserProfile = JSON.parse(raw)
      setUser(parsed)
      setForm({ fullname: parsed.fullname || '', phone: parsed.phone || '' })
    } catch { navigate('/login') }
  }, [navigate])

  // Load interview list
  useEffect(() => {
    interviewService.list()
      .then(res => setInterviews(res.data?.data || []))
      .catch(() => setInterviews([]))
      .finally(() => setIvLoading(false))
  }, [])

  useEffect(() => {
    http.get('auth/my-applications')
      .then((response) => {
        const applications = response.data?.data
        setApplicationCount(Array.isArray(applications) ? applications.length : 0)
      })
      .catch(() => setApplicationCount(0))
  }, [])

  const showToast = (type: 'success' | 'error', msg: string) => {
    setToast({ type, msg })
    setTimeout(() => setToast(null), 3500)
  }

  const handleSave = async () => {
    if (!user) return
    if (!form.fullname.trim()) { showToast('error', 'Nama tidak boleh kosong'); return }
    setSaving(true)
    try {
      await http.put('user/', {
        user_id: user.user_id,
        fullname: form.fullname.trim(),
        phone: form.phone.trim()
      })
      const updated = { ...user, fullname: form.fullname.trim(), phone: form.phone.trim() }
      setUser(updated)
      localStorage.setItem('authUser', JSON.stringify(updated))
      window.dispatchEvent(new Event('storage'))
      setIsEditing(false)
      showToast('success', 'Profil berhasil disimpan')
    } catch {
      showToast('error', 'Gagal menyimpan profil. Coba lagi.')
    } finally {
      setSaving(false)
    }
  }

  const handleCancel = () => {
    if (!user) return
    setForm({ fullname: user.fullname || '', phone: user.phone || '' })
    setIsEditing(false)
  }

  if (!user) return null

  const completedIvs = interviews.filter(iv => iv.status === 'completed')
  const { steps, pct } = calcCompletion(user, applicationCount, completedIvs.length)
  const recentIvs = interviews.slice(0, 5)

  return (
    <div className="prf-page">
      {toast && (
        <div className={`prf-toast prf-toast--${toast.type}`}>{toast.msg}</div>
      )}

      <div className="prf-layout">

        {/* ── LEFT COLUMN ─────────────────────────── */}
        <aside className="prf-sidebar">

          {/* Identity card */}
          <div className="prf-card prf-card--identity">
            <img
              src={defaultProfile}
              alt="Foto profil"
              className="prf-avatar"
            />

            {isEditing ? (
              <input
                className="prf-input prf-input--name"
                value={form.fullname}
                onChange={e => setForm(f => ({ ...f, fullname: e.target.value }))}
                placeholder="Nama lengkap"
                autoFocus
              />
            ) : (
              <h1 className="prf-name">{user.fullname || '-'}</h1>
            )}

            <p className="prf-email"><FiMail size={13} /> {user.email}</p>

            {/* Phone row */}
            <div className="prf-detail-row">
              <span className="prf-detail-label"><FiPhone size={13} /> No. HP</span>
              {isEditing ? (
                <input
                  className="prf-input"
                  value={form.phone}
                  onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                  placeholder="Nomor HP"
                  type="tel"
                />
              ) : (
                <span className="prf-detail-value">{user.phone || <span className="prf-empty">Belum diisi</span>}</span>
              )}
            </div>

            {/* Action buttons */}
            {isEditing ? (
              <div className="prf-edit-actions">
                <button className="prf-btn prf-btn--ghost" onClick={handleCancel} disabled={saving}>
                  <FiX size={14} /> Batal
                </button>
                <button className="prf-btn prf-btn--primary" onClick={handleSave} disabled={saving}>
                  {saving ? <span className="prf-spinner" /> : <FiCheck size={14} />}
                  {saving ? 'Menyimpan...' : 'Simpan'}
                </button>
              </div>
            ) : (
              <button className="prf-btn prf-btn--outline prf-btn--full" onClick={() => setIsEditing(true)}>
                <FiEdit2 size={14} /> Edit Profil
              </button>
            )}
          </div>

          {/* Stats */}
          <div className="prf-card prf-stats">
            <div className="prf-stat">
              <span className="prf-stat__num">{applicationCount}</span>
              <span className="prf-stat__lbl">Total Lamaran</span>
            </div>
            <div className="prf-stat-divider" />
            <div className="prf-stat">
              <span className="prf-stat__num">{completedIvs.length}</span>
              <span className="prf-stat__lbl">Interview Selesai</span>
            </div>
            <div className="prf-stat-divider" />
            <div className="prf-stat">
              <span className="prf-stat__num">
                {completedIvs.length > 0
                  ? Math.round(completedIvs.reduce((s, iv) => s + (iv.result?.final_score || 0), 0) / completedIvs.length)
                  : '-'}
              </span>
              <span className="prf-stat__lbl">Rata-rata Skor</span>
            </div>
          </div>

        </aside>

        {/* ── RIGHT COLUMN ─────────────────────────── */}
        <main className="prf-main">

          <CandidateProfileEditor />

          {/* Profile completion */}
          <div className="prf-card prf-completion">
            <div className="prf-completion__header">
              <div>
                <h3 className="prf-section-title">Kelengkapan Profil</h3>
                <p className="prf-completion__sub">Lengkapi profil untuk meningkatkan peluang.</p>
              </div>
              <span className="prf-completion__pct">{pct}%</span>
            </div>

            <div className="prf-progress-track">
              <div className="prf-progress-bar" style={{ width: `${pct}%` }} />
            </div>

            <ul className="prf-checklist">
              {steps.map(s => (
                <li key={s.label} className={`prf-checklist__item ${s.done ? 'prf-checklist__item--done' : ''}`}>
                  <span className="prf-checklist__dot" />
                  {s.label}
                </li>
              ))}
            </ul>
          </div>

          {/* Riwayat Lamaran */}
          <div className="prf-card">
            <div className="prf-section-head">
              <h3 className="prf-section-title"><FiBriefcase size={16} /> Riwayat Lamaran</h3>
              {applicationCount > 5 && (
                <button className="prf-link" onClick={() => navigate('/status-lamaran')}>
                  Lihat semua <FiChevronRight size={13} />
                </button>
              )}
            </div>

            {ivLoading ? (
              <div className="prf-skeleton-list">
                {[1,2,3].map(i => <div key={i} className="prf-skeleton-row" />)}
              </div>
            ) : recentIvs.length === 0 ? (
              <div className="prf-empty-state">
                <FiBriefcase size={32} />
                <p>Belum ada lamaran.</p>
                <button className="prf-btn prf-btn--primary prf-btn--sm" onClick={() => navigate('/lowongan-kerja')}>
                  Cari Lowongan
                </button>
              </div>
            ) : (
              <ul className="prf-iv-list">
                {recentIvs.map(iv => (
                  (() => {
                    const interviewTimestamp = resolveInterviewTimestampMeta(iv)
                    return (
                      <li key={iv.id} className="prf-iv-item">
                        <div className="prf-iv-item__icon">
                          {iv.status === 'completed' ? <FiAward size={18} /> : <FiClock size={18} />}
                        </div>
                        <div className="prf-iv-item__body">
                          <p className="prf-iv-item__job">{iv.job?.title || 'Posisi tidak tersedia'}</p>
                          <p className="prf-iv-item__date">
                            {interviewTimestamp ? `${interviewTimestamp.label}: ${interviewTimestamp.display}` : '-'}
                          </p>
                        </div>
                        <div className="prf-iv-item__right">
                          <span className={badgeClass(iv.status === 'completed' ? (iv.result?.status || 'done') : 'pending')}>
                            {badgeLabel(iv)}
                          </span>
                          {iv.result && (
                            <span className="prf-iv-item__score">{iv.result.final_score}/100</span>
                          )}
                          {iv.status === 'completed' && (
                            <button
                              className="prf-link prf-link--sm"
                              onClick={() => navigate(`/interview/detail/${iv.job?.id || iv.id}`, {
                                state: {
                                  interviewId: iv.id,
                                  jobId: iv.job?.id || null,
                                },
                              })}
                            >
                              Detail <FiChevronRight size={12} />
                            </button>
                          )}
                        </div>
                      </li>
                    )
                  })()
                ))}
              </ul>
            )}
          </div>

        </main>
      </div>
    </div>
  )
}

export default Profile
